import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import {
    Container,
    Typography,
    Card,
    CardContent,
    Box,
    Button,
    makeStyles,
    CircularProgress
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { 
    ArrowLeft, 
    CheckCircle, 
    Clock,
    Sparkles,
    Bell
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useUser } from '../../contexts/UserContext';
import { startTrainingJob } from '../../utils/trainingService';
import { getLatestUploadSession } from '../../utils/storageService';
import { useTrainingJobListener } from '../../hooks/useTrainingJobListener';

const useStyles = makeStyles((theme) => ({
    root: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        paddingTop: theme.spacing(4),
        paddingBottom: theme.spacing(4),
    },
    container: {
        maxWidth: '800px',
    },
    header: {
        textAlign: 'center',
        color: 'white',
        marginBottom: theme.spacing(4),
    },
    title: {
        fontWeight: 700,
        marginBottom: theme.spacing(1),
        fontSize: '2.5rem',
        [theme.breakpoints.down('sm')]: {
            fontSize: '2rem',
        },
    },
    card: {
        borderRadius: 16,
        boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
        marginBottom: theme.spacing(3),
    },
    backButton: {
        color: 'white',
        marginBottom: theme.spacing(2),
        '&:hover': {
            backgroundColor: 'rgba(255,255,255,0.1)',
        },
    },
    actionButton: {
        padding: theme.spacing(1.5, 4),
        fontSize: '1rem',
        fontWeight: 600,
        borderRadius: 25,
        marginTop: theme.spacing(2),
    },
    primaryButton: {
        background: 'linear-gradient(45deg, #4CAF50 30%, #45a049 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #4CAF50 60%, #45a049 100%)',
            transform: 'scale(1.02)',
        },
    },
    secondaryButton: {
        background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
        color: 'white',
        '&:hover': {
            background: 'linear-gradient(45deg, #667eea 60%, #764ba2 100%)',
            transform: 'scale(1.02)',
        },
    },
}));

const TrainingProgress = () => {
    const classes = useStyles();
    const history = useHistory();
    const { currentUser } = useAuth();
    const { userProfile } = useUser();
    
    const [error, setError] = useState(null);
    const [notifyEnabled, setNotifyEnabled] = useState(false);
    
    // Use real-time training job listener
    const { trainingJob, loading } = useTrainingJobListener(currentUser?.uid);

    const handleBack = () => {
        history.push('/upload');
    };

    const handleStartGeneration = () => {
        history.push('/generate');
    };

    const handleNotifyMe = () => {
        setNotifyEnabled(true);
    };

    const handleStartTraining = async () => {
        try {
            setError(null);
            
            // Get the latest upload session
            const uploadSession = await getLatestUploadSession(currentUser.uid);
            if (!uploadSession) {
                setError('No upload session found. Please upload photos first.');
                return;
            }
            
            // Start training job
            const response = await startTrainingJob(currentUser.uid, uploadSession.id);
            console.log('Started training job:', response);
            
        } catch (err) {
            setError(err.message);
        }
    };

    // Real-time listener is handled by useTrainingJobListener hook
    // No need for polling anymore!

    const isTrainingComplete = trainingJob?.status === 'completed';
    const isTrainingInProgress = trainingJob && ['starting', 'queued', 'training', 'initializing'].includes(trainingJob.status);

    return (
        <Box className={classes.root}>
            <Container className={classes.container}>
                <Button
                    startIcon={<ArrowLeft size={20} />}
                    onClick={handleBack}
                    className={classes.backButton}
                >
                    Back to Upload
                </Button>

                <Box className={classes.header}>
                    <Typography variant="h1" className={classes.title}>
                        {isTrainingComplete ? 'Training Complete!' : 'Training Your AI Model'}
                    </Typography>
                    <Typography variant="body1" style={{ opacity: 0.8 }}>
                        Welcome, {userProfile?.name || currentUser?.email}! You have {userProfile?.tokens || 0} tokens.
                    </Typography>
                </Box>

                {/* Error Message */}
                {error && (
                    <Alert severity="error" style={{ marginBottom: 24 }}>
                        {error}
                    </Alert>
                )}

                {/* Main Message Card */}
                <Card className={classes.card}>
                    <CardContent>
                        <Box textAlign="center" padding={4}>
                            {loading ? (
                                <>
                                    <CircularProgress size={60} style={{ marginBottom: 24, color: '#667eea' }} />
                                    <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
                                        Getting Started...
                                    </Typography>
                                </>
                            ) : isTrainingComplete ? (
                                <>
                                    <CheckCircle size={60} color="#4CAF50" style={{ marginBottom: 24 }} />
                                    <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
                                        🎉 Your AI model is ready!
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" style={{ marginBottom: 32 }}>
                                        We've successfully created a digital representation of your photos. 
                                        You can now generate unlimited AI photos using your personalized model.
                                    </Typography>
                                    <Button
                                        className={`${classes.actionButton} ${classes.primaryButton}`}
                                        size="large"
                                        onClick={handleStartGeneration}
                                        startIcon={<Sparkles size={20} />}
                                    >
                                        Start Generating Photos
                                    </Button>
                                </>
                            ) : isTrainingInProgress ? (
                                <>
                                    <Clock size={60} color="#667eea" style={{ marginBottom: 24 }} />
                                    <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
                                        Training in Progress
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" style={{ marginBottom: 32 }}>
                                        We're generating a digital representation of the photos you uploaded to power our AI photo generation. 
                                        This usually takes 15-20 minutes.
                                    </Typography>
                                    {notifyEnabled ? (
                                        <Box>
                                            <CheckCircle size={24} color="#4CAF50" style={{ marginBottom: 8 }} />
                                            <Typography variant="body2" color="textSecondary">
                                                We'll notify you when training is complete!
                                            </Typography>
                                        </Box>
                                    ) : (
                                        <Button
                                            className={`${classes.actionButton} ${classes.secondaryButton}`}
                                            size="large"
                                            onClick={handleNotifyMe}
                                            startIcon={<Bell size={20} />}
                                        >
                                            Notify Me When It's Done
                                        </Button>
                                    )}
                                </>
                            ) : (
                                <>
                                    <Clock size={60} color="#667eea" style={{ marginBottom: 24 }} />
                                    <Typography variant="h5" style={{ marginBottom: 16, fontWeight: 600 }}>
                                        Ready to Start Training
                                    </Typography>
                                    <Typography variant="body1" color="textSecondary" style={{ marginBottom: 32 }}>
                                        Click below to start training your personalized AI model. 
                                        This process usually takes 15-20 minutes.
                                    </Typography>
                                    <Button
                                        className={`${classes.actionButton} ${classes.primaryButton}`}
                                        size="large"
                                        onClick={handleStartTraining}
                                        startIcon={<Sparkles size={20} />}
                                        disabled={loading}
                                    >
                                        {loading ? 'Starting...' : 'Start Training'}
                                    </Button>
                                </>
                            )}
                        </Box>
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default TrainingProgress;